// @ts-nocheck
import React, { useState } from 'react';

interface CoinLogoImageProps {
  src: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Reusable coin logo image component with fallback handling
 *
 * Features:
 * - Displays coin logo from API
 * - Falls back to first letter of coin symbol if image fails to load
 * - Smooth fade-in animation
 * - Prevents flickering with proper error handling
 */
const CoinLogoImage: React.FC<CoinLogoImageProps> = ({
  src,
  alt,
  size = 'sm',
  className = ''
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log(`CoinLogoImage: ${alt} - src: ${src}, error: ${imageError}, loaded: ${imageLoaded}`);
  }, [src, alt, imageError, imageLoaded]);

  // Size mapping
  const sizeClasses = {
    sm: 'w-5 h-5 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.sm;

  // If image fails or src is empty, show fallback
  if (!src || imageError) {
    const firstLetter = alt.charAt(0).toUpperCase();
    return (
      <div
        className={`${sizeClass} ${className} rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center font-bold text-white`}
        title={alt}
      >
        {firstLetter}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${sizeClass} ${className} rounded-full ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
      onError={() => setImageError(true)}
      onLoad={() => setImageLoaded(true)}
      loading="lazy"
    />
  );
};

export default CoinLogoImage;
